import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { DiscardChangesDialog } from '@/components/DiscardChangesDialog/DiscardChangesDialog';
import {
  PageLayout,
  PageLayoutContent,
  PageLayoutLeftPart,
  PageLayoutLeftPartContent,
  PageLayoutSimpleHeader,
} from '@/components/PageLayout';
import AlertBar from '@/design_system/AlertBar';
import Button from '@/design_system/Button';
import { Footer, FooterButtons } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import {
  canBeCreatedBy,
  ShipmentCarrierService,
  ShipmentHandover,
  ShipmentWithRelations,
  useShipment,
  useUpdateDraftShipment,
} from '@/models/shipment';
import ShipmentForm from '@/routes/Shipments/Shipment/components/ShipmentForm';
import { useCurrentSession } from '@/services/auth';
import { useShowWarningBeforeLeavingApp, useSimpleBlocker } from '@/utils/navigation';
import { useScrollIntoView } from '@/utils/useScrollIntoView';
import useViewPort from '@/utils/useViewport';

import {
  getShipmentFormArticles,
  ShipmentFormArticle,
} from '../components/ShipmentForm/components/ArticleSelect/ArticleSelect';
import {
  newShipmentFormPlace,
  ShipmentFormPlace,
} from '../components/ShipmentForm/components/PlaceSelect/PlaceSelect';

const Edit = () => {
  const blocker = useSimpleBlocker();

  useShowWarningBeforeLeavingApp();

  return (
    <>
      <EditPageWrapper />
      {blocker.state === 'blocked' && (
        <DiscardChangesDialog
          onConfirm={() => blocker.proceed()}
          onCancel={() => blocker.reset()}
        />
      )}
    </>
  );
};

const EditPageWrapper = () => {
  const { id: shipmentId } = useParams();
  const { data: shipment, isLoading } = useShipment(shipmentId);
  const navigate = useNavigate();
  const { currentSession } = useCurrentSession();

  if (isLoading) {
    return null;
  }

  if (!shipment) {
    navigate('/shipments', { state: { skipRouterBlocker: true }, replace: true });

    return null;
  }

  if (shipment.status !== 'draft') {
    navigate(`/shipments/${shipment.id}`, { state: { skipRouterBlocker: true }, replace: true });

    return null;
  }

  if (!canBeCreatedBy(shipment, currentSession)) {
    navigate('/shipments', { state: { skipRouterBlocker: true }, replace: true });

    return null;
  }

  return <EditPage shipment={shipment} />;
};

const EditPage = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { isMobile } = useViewPort();

  const [origin, setOrigin] = useState<ShipmentFormPlace | undefined>(
    shipment.originAddress
      ? newShipmentFormPlace(shipment.originAddress, shipment.originClient, shipment.originEntity)
      : undefined
  );
  const [destination, setDestination] = useState<ShipmentFormPlace | undefined>(
    shipment.destinationAddress
      ? newShipmentFormPlace(
          shipment.destinationAddress,
          shipment.destinationClient,
          shipment.destinationEntity
        )
      : undefined
  );
  const [articles, setArticles] = useState<ShipmentFormArticle[]>(
    getShipmentFormArticles(shipment)
  );
  const [carrierService, setCarrierService] = useState<ShipmentCarrierService | undefined>(
    shipment.carrierService ?? undefined
  );
  const [handover, setHandover] = useState<ShipmentHandover | undefined>(
    shipment.handover ?? undefined
  );
  const [pickupDate, setPickupDate] = useState<string | undefined>(
    shipment.pickupDate ?? undefined
  );
  const [trackingId, setTrackingId] = useState<string | undefined>(
    shipment.trackingId ?? undefined
  );
  const [error, setError] = useState<string>();
  const [alertRef, scrollAlertIntoView] = useScrollIntoView<HTMLDivElement>();

  const { mutateAsync: updateDraftShipment, isPending: isUpdateDraftShipmentPending } =
    useUpdateDraftShipment(shipment.id);

  const handleError = (error: string) => {
    setError(error);
    scrollAlertIntoView();
  };

  const onSubmit = () => {
    if (!origin) {
      handleError(t({ id: 'shipments.new.error.origin', message: 'Please select an origin' }));

      return;
    }

    if (!destination) {
      handleError(
        t({ id: 'shipments.new.error.destination', message: 'Please select a destination' })
      );

      return;
    }

    if (articles.length === 0) {
      handleError(
        t({ id: 'shipments.new.error.articles', message: 'Please add at least one item' })
      );

      return;
    }

    if (!carrierService) {
      handleError(t({ id: 'shipments.new.error.carrier', message: 'Please select a carrier' }));

      return;
    }

    if (!!carrierService && carrierService !== 'private-carrier' && !handover) {
      handleError(
        t({
          id: 'shipments.new.error.handover',
          message: 'Please select a handover option',
        })
      );

      return;
    }

    if (handover === 'pickup' && carrierService !== 'private-carrier' && !pickupDate) {
      handleError(
        t({
          id: 'shipments.new.error.pickup-date',
          message: 'Please select a pickup date',
        })
      );

      return;
    }

    updateDraftShipment({
      origin: origin.client?.id ?? origin.entity?.id ?? '',
      destination: destination.client?.id ?? destination.entity?.id ?? '',
      articles: articles.map((article) => article.id),
      carrierService,
      handover: carrierService === 'private-carrier' ? 'pickup' : handover,
      pickupDate:
        handover === 'pickup' && carrierService !== 'private-carrier' ? pickupDate : undefined,
      trackingId: carrierService === 'private-carrier' ? trackingId : undefined,
    })
      .then((shipment) => {
        navigate(`/shipments/${shipment.id}`, {
          state: { skipRouterBlocker: true },
          replace: true,
        });
      })
      .catch((err) => {
        console.error(err);
        setError(
          (err.message as string) ??
            t({
              id: 'shipment.unknown-error',
              message: 'Unknown error. Please contact Support',
            })
        );
      });
  };

  return (
    <PageLayout>
      <PageLayoutSimpleHeader
        title={t({ id: 'shipments.edit.title', message: 'Edit shipment' })}
        onClose={() =>
          navigate(`/shipments/${shipment.id}`, {
            state: { skipRouterBlocker: false },
            replace: true,
          })
        }
      />
      <PageLayoutContent>
        <PageLayoutLeftPart>
          <PageLayoutLeftPartContent>
            <Stack padding={isMobile ? '1rem' : undefined} ref={alertRef} gap="1rem">
              {!!error && (
                <AlertBar
                  type="error"
                  title={t({
                    id: 'shipments.edit.error',
                    message:
                      'Please fill the missing information in order to be able to edit the shipment',
                  })}
                />
              )}
              <ShipmentForm
                {...{
                  origin,
                  setOrigin,
                  destination,
                  setDestination,
                  articles,
                  setArticles,
                  carrierService,
                  setCarrierService,
                  handover,
                  setHandover,
                  pickupDate,
                  setPickupDate,
                  trackingId,
                  setTrackingId,
                  error,
                }}
              />
            </Stack>
          </PageLayoutLeftPartContent>

          <Footer>
            <FooterButtons>
              <Button variant="primary" onPress={onSubmit} isLoading={isUpdateDraftShipmentPending}>
                <Trans id="shipments.edit.save">Save</Trans>
              </Button>
            </FooterButtons>
          </Footer>
        </PageLayoutLeftPart>
      </PageLayoutContent>
    </PageLayout>
  );
};

export default Edit;
